import { z } from 'zod';
import type { BlockDefinition } from '../types';

export const timelineSchema = z.object({
  eyebrow: z.string().max(120).optional(),
  h2: z.string().max(200).optional(),
  subtitle: z.string().max(500).optional(),
  orientation: z.enum(['vertical', 'horizontal']).default('vertical'),
  items: z
    .array(
      z.object({
        date: z.string().min(1).max(60),
        title: z.string().min(1).max(160),
        description: z.string().max(500).optional(),
        icon: z.string().max(60).optional(),
      }),
    )
    .min(2)
    .max(20),
});

export type TimelineData = z.infer<typeof timelineSchema>;

export function TimelineRenderer({ eyebrow, h2, subtitle, orientation = 'vertical', items }: TimelineData) {
  const safe = Array.isArray(items) ? items : [];
  return (
    <section className="v5-section v5-timeline-section">
      <div className="container">
        {(eyebrow || h2) && (
          <div className="v5-section-head">
            {eyebrow ? <div className="v5-eyebrow">{eyebrow}</div> : null}
            {h2 ? <h2 className="v5-h2">{h2}</h2> : null}
            {subtitle ? <p className="v5-lead">{subtitle}</p> : null}
          </div>
        )}
        <ol className={`v5-timeline v5-timeline-${orientation}`}>
          {safe.map((it, i) => (
            <li key={i} className="v5-timeline-item">
              <div className="v5-timeline-dot" aria-hidden>{it.icon ? it.icon[0]?.toUpperCase() : i + 1}</div>
              <div className="v5-timeline-content">
                <div className="v5-timeline-date">{it.date}</div>
                <h3 className="v5-timeline-title">{it.title}</h3>
                {it.description ? <p className="v5-timeline-desc">{it.description}</p> : null}
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

export const timelineBlock: BlockDefinition<TimelineData> = {
  meta: {
    type: 'timeline',
    label: 'Línea de tiempo / Roadmap',
    description: 'Lista cronológica de hitos. Vertical (clásica) u horizontal (scroll).',
    category: 'content',
    icon: 'history',
  },
  schema: timelineSchema,
  defaults: {
    eyebrow: 'NUESTRA HISTORIA',
    h2: 'Hitos que nos definen',
    orientation: 'vertical',
    items: [
      { date: '2020', title: 'Fundación', description: 'Empezamos con 3 personas y una idea.' },
      { date: '2022', title: 'Primer hito', description: 'Lanzamos producto v1 y cerramos primera ronda.' },
      { date: '2024', title: 'Escala', description: 'Internacionalización y +50 empleados.' },
      { date: '2026', title: 'Hoy', description: 'Líderes del sector con presencia en 12 países.' },
    ],
  },
  fields: [
    { name: 'eyebrow', label: 'Eyebrow', type: 'text', max: 120 },
    { name: 'h2', label: 'Título', type: 'text', max: 200 },
    { name: 'subtitle', label: 'Subtítulo', type: 'textarea', max: 500 },
    {
      name: 'orientation',
      label: 'Orientación',
      type: 'select',
      options: [
        { label: 'Vertical', value: 'vertical' },
        { label: 'Horizontal (scroll)', value: 'horizontal' },
      ],
    },
    {
      name: 'items',
      label: 'Hitos',
      type: 'array',
      max: 20,
      itemFields: [
        { name: 'date', label: 'Fecha / etiqueta', type: 'text', required: true, max: 60 },
        { name: 'title', label: 'Título', type: 'text', required: true, max: 160 },
        { name: 'description', label: 'Descripción', type: 'textarea', max: 500 },
        { name: 'icon', label: 'Icono (opcional)', type: 'text', max: 60 },
      ],
    },
  ],
  Renderer: TimelineRenderer,
};
